import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosClient, { publicAxios } from '../../axios'

// ── Types ────────────────────────────────────────────────────────────────

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked'
export type InvitationPlanTier = 'free' | 'pro' | 'premium' | 'enterprise'

export interface InvitationOrganization {
  id: string
  officialName: string
  type: 'chu' | 'ch' | 'private_lab' | 'clinic' | 'research_center' | 'other'
}

export interface Invitation {
  id: string
  email: string
  status: InvitationStatus
  planTier: InvitationPlanTier
  organization: InvitationOrganization | null
  sentBy: { id: string; contactName: string | null; email: string } | null
  acceptedBy: {
    id: string
    contactName: string | null
    email: string
    /** Bounded-access window for invited pilots. ISO date or null = unlimited. */
    accessExpiresAt: string | null
  } | null
  sentAt: string
  expiresAt: string
  acceptedAt: string | null
  message: string | null
  createdAt: string
}

export interface InvitationsListResponse {
  data: Invitation[]
  meta: { page: number; limit: number; totalItems: number; totalPages: number }
}

export interface CreateInvitationPayload {
  email: string
  /** Either `organizationId` (pick an existing one) … */
  organizationId?: string
  /** … or `organizationName` to create one on the fly (default type = 'chu'). */
  organizationName?: string
  organizationType?: InvitationOrganization['type']
  organizationCity?: string
  planTier: InvitationPlanTier
  message?: string
  /** Override the default 14-day expiry. */
  ttlDays?: number
  /** Pilot access window in days, applied at activation. Omitted → 90. */
  accessDays?: number
}

export interface VerifyInvitationResponse {
  email: string
  planTier: InvitationPlanTier
  organization: InvitationOrganization | null
  expiresAt: string
  message: string | null
}

// ── Query keys ───────────────────────────────────────────────────────────

const invitationsKey = (status?: InvitationStatus, search?: string, limit?: number) =>
  ['admin', 'invitations', { status: status ?? null, search: search ?? null, limit: limit ?? null }] as const

// ── Hooks ────────────────────────────────────────────────────────────────

/**
 * List invitations (admin only). The backend already does real server-side
 * pagination (page/limit, default 50 — see admin-invitations.js) and
 * returns totalItems/totalPages; `limit` just wasn't wired up here before —
 * same "grow the limit" pattern as PilotDetailPanel's "Charger plus de cas".
 */
export function useInvitations(opts: { status?: InvitationStatus; search?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: invitationsKey(opts.status, opts.search, opts.limit),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (opts.status) params.set('status', opts.status)
      if (opts.search) params.set('search', opts.search)
      if (opts.limit) params.set('limit', String(opts.limit))
      const { data } = await axiosClient.get<InvitationsListResponse>(
        `/admin/invitations${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return data
    },
    staleTime: 30_000,
  })
}

export function useCreateInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateInvitationPayload) => {
      const { data } = await axiosClient.post<Invitation>('/admin/invitations', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] })
    },
  })
}

export function useResendInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ttlDays }: { id: string; ttlDays?: number }) => {
      const { data } = await axiosClient.post<Invitation>(`/admin/invitations/${id}/resend`, { ttlDays })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] })
    },
  })
}

/**
 * Hard-delete an invitation row. Revoking keeps the audit trail, which is
 * right for a real invitation and pure noise for a throwaway test address.
 * Refused server-side on an accepted invitation (an account exists behind it).
 */
export function usePurgeInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosClient.delete(`/admin/invitations/${id}/purge`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] })
    },
  })
}

export function useRevokeInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosClient.delete(`/admin/invitations/${id}`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] })
    },
  })
}

/**
 * Public — verify an invitation token from the register page.
 * Uses publicAxios because the recipient is not logged in.
 */
export async function verifyInvitationToken(token: string): Promise<VerifyInvitationResponse> {
  const { data } = await publicAxios.get<VerifyInvitationResponse>(
    `/admin/invitations/verify?token=${encodeURIComponent(token)}`,
  )
  return data
}

/**
 * Update (extend / clear) a user's bounded-access window.
 * Pass `accessExpiresAt: null` to grant unlimited access.
 */
export function useUpdateUserAccessExpiry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, accessExpiresAt }: { userId: string; accessExpiresAt: string | null }) => {
      const { data } = await axiosClient.patch(`/users/${userId}/access-expiry`, { accessExpiresAt })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] })
    },
  })
}
